import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContributorCategoryToken } from '@sleed/bizcommerce-api';
import { contributorCategoriesApi } from '~/lib/api/client';
import { formToToken, type ContributorCategoryFormValues } from '~/lib/schemas/contributor-category';

export const contributorCategoryKeys = {
	all: ['contributor-categories'] as const,
	list: () => [...contributorCategoryKeys.all, 'list'] as const
};

/** All contributor categories (Κατηγορίες Συντελεστών) — small lookup set, loaded in full. */
export function useContributorCategoriesList() {
	return useQuery<ContributorCategoryToken[]>({
		queryKey: contributorCategoryKeys.list(),
		queryFn: async () => (await contributorCategoriesApi.getAll()).data.items ?? []
	});
}

export function useContributorCategoryMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: contributorCategoryKeys.list() });
	const create = useMutation({
		mutationFn: async (form: ContributorCategoryFormValues) => (await contributorCategoriesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		// Spread the original token first so server-owned fields not on the form (is_cust, is_default,
		// btrl) survive the round-trip.
		mutationFn: async ({ form, original }: { form: ContributorCategoryFormValues; original: ContributorCategoryToken | null }) =>
			(await contributorCategoriesApi.update(formToToken(form, original))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await contributorCategoriesApi.remove(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
